import { useEffect, useState } from 'react'
import { debug, logError } from 'lib/logging'

export function useDeferredData<T>(promise?: Promise<T> | null, name?: string): T | undefined {
  debug(`defer`, `useDeferredData`, promise, name ? name : ``)
  let [data, setData] = useState<T | undefined>(undefined)
  useEffect(() => {
    const getData = async () => {
      let d = await promise
      debug(`defer`, `got data`, d, name ? name : ``)
      if (d) setData(d)
    }
    getData().catch((e) => {
      logError(`error getting deferred data`, e)
      // document.location = `/`
    })
  }, [promise])
  return data
}

export function useDeferredDataWithError<T>(promise?: Promise<T> | null): { data: T | undefined; error: Error | null } {
  const [data, setData] = useState<T | undefined>(undefined)
  const [error, setError] = useState<Error | null>(null)
  useEffect(() => {
    setError(null)
    const getData = async () => {
      let d = await promise
      debug(`defer`, `got data`, d)
      if (d) setData(d)
    }
    getData().catch((e) => {
      logError(`error getting deferred data`, e)
      setError(e)
    })
  }, [promise])
  return { data, error }
}

export function useDeferredDataWithStatus<T>(
  promise?: Promise<T> | null,
  slowThresholdMs = 5000,
): { data: T | undefined; error: Error | null; isSlow: boolean } {
  const [data, setData] = useState<T | undefined>(undefined)
  const [error, setError] = useState<Error | null>(null)
  const [isSlow, setIsSlow] = useState(false)

  useEffect(() => {
    setError(null)
    setIsSlow(false)
    setData(undefined)

    const timeout = setTimeout(() => setIsSlow(true), slowThresholdMs)

    const getData = async () => {
      const d = await promise
      debug(`defer`, `got data`, d)
      clearTimeout(timeout)
      if (d) setData(d)
      setIsSlow(false)
    }
    getData().catch((e) => {
      logError(`error getting deferred data`, e)
      clearTimeout(timeout)
      setError(e)
      setIsSlow(false)
    })

    return () => clearTimeout(timeout)
  }, [promise, slowThresholdMs])

  return { data, error, isSlow }
}
